package com.example.borshi.web.nutritionist.repository

import com.example.borshi.web.nutritionist.models.*

/**
 * Repository interface for Nutritionist data operations
 * This abstraction allows for easy testing and future backend changes
 * Part of the Nutritionist Module - can be extracted as independent module
 */
interface NutritionistRepository {

    // Profile Management
    suspend fun getNutritionistProfile(nutritionistId: String): Result<NutritionistProfile>
    suspend fun updateNutritionistProfile(profile: NutritionistProfile): Result<Unit>

    // Dashboard Stats
    suspend fun getDashboardStats(nutritionistId: String): Result<DashboardStats>

    // Patient Management
    suspend fun getPatients(nutritionistId: String): Result<List<Patient>>
    suspend fun getPatient(patientId: String): Result<Patient>
    suspend fun createUser(patient: Patient): Result<String>  // Returns generated userId
    suspend fun updatePatient(patient: Patient): Result<Unit>
    suspend fun getPatientProgress(patientId: String, limit: Int = 10): Result<List<PatientProgress>>
    suspend fun addPatientProgress(progress: PatientProgress): Result<Unit>
    suspend fun updatePatientProgress(progress: PatientProgress): Result<Unit>
    suspend fun deletePatientProgress(progressId: String): Result<Unit>


    // Alerts
    suspend fun getAlerts(nutritionistId: String, unreadOnly: Boolean = false): Result<List<PatientAlert>>
    suspend fun markAlertAsRead(alertId: String): Result<Unit>

    // Plans Management
    suspend fun getBasePlans(nutritionistId: String): Result<List<NutritionistBasePlan>>
    suspend fun getBasePlan(planId: String): Result<NutritionistBasePlan>
    suspend fun createBasePlan(plan: NutritionistBasePlan): Result<String>
    suspend fun updateBasePlan(plan: NutritionistBasePlan): Result<Unit>
    suspend fun deleteBasePlan(planId: String): Result<Unit>
    suspend fun assignPlanToPatient(
        patientId: String,
        basePlanId: String,
        startDate: String,
        deliveryDays: Set<String>,
        deliveryTime: String,
        customNotes: String = ""
    ): Result<String>  // Returns personalizedPlanId

    // Food Catalog
    suspend fun searchProducts(query: String, filters: Map<String, Any> = emptyMap()): Result<List<ProductItem>>
    suspend fun getProductDetails(productId: String): Result<ProductItem>

    // Clinical Notes
    suspend fun getClinicalNotes(patientId: String, limit: Int = 20): Result<List<ClinicalNote>>
    suspend fun addClinicalNote(note: ClinicalNote): Result<String>  // Returns noteId
    suspend fun updateClinicalNote(note: ClinicalNote): Result<Unit>
    suspend fun deleteClinicalNote(noteId: String): Result<Unit>

    // Messaging
    suspend fun getConversations(nutritionistId: String): Result<List<Conversation>>
    suspend fun getMessages(conversationId: String, limit: Int = 50): Result<List<Message>>
    suspend fun sendMessage(message: Message): Result<String>  // Returns messageId
    suspend fun markConversationAsRead(conversationId: String, userId: String): Result<Unit>

    // Biomarkers
    suspend fun getBiomarkers(patientId: String, biomarkerType: String? = null): Result<List<Biomarker>>
    suspend fun addBiomarker(biomarker: Biomarker): Result<String>  // Returns biomarkerId
    suspend fun updateBiomarker(biomarker: Biomarker): Result<Unit>
    suspend fun deleteBiomarker(biomarkerId: String): Result<Unit>
}

/**
 * Biomarker data for health tracking
 */
data class Biomarker(
    val biomarkerId: String = "",
    val patientId: String = "",
    val nutritionistId: String = "",
    val biomarkerType: String = "",  // glucose, cholesterol, vitamin_d, hemoglobin, vitamin_b12
    val value: Double = 0.0,
    val unit: String = "",
    val notes: String = "",
    val isNormal: Boolean = true,
    val timestamp: Long = 0,
    val createdAt: Long = 0
)

/**
 * Conversation between nutritionist and patient
 */
data class Conversation(
    val conversationId: String = "",
    val participants: List<String> = emptyList(), // [nutritionistId, patientId]
    val participantNames: Map<String, String> = emptyMap(),
    val lastMessage: String = "",
    val lastMessageTime: Long = 0,
    val unreadCount: Map<String, Int> = emptyMap(), // userId -> count
    val createdAt: Long = 0
)

/**
 * Message in a conversation
 */
data class Message(
    val messageId: String = "",
    val conversationId: String = "",
    val senderId: String = "",
    val senderName: String = "",
    val content: String = "",
    val messageType: String = "text", // text, image, document
    val timestamp: Long = 0,
    val readBy: List<String> = emptyList()
)

/**
 * Clinical note for patient documentation
 */
data class ClinicalNote(
    val noteId: String = "",
    val patientId: String = "",
    val nutritionistId: String = "",
    val nutritionistName: String = "",
    val noteType: String = "General", // Consultation, Observation, Feedback, General
    val content: String = "",
    val tags: List<String> = emptyList(),
    val createdAt: Long = 0,
    val updatedAt: Long = 0
)

/**
 * Product item for food catalog
 */
data class ProductItem(
    val productId: String = "",
    val name: String = "",
    val category: String = "",
    val tags: List<String> = emptyList(),
    val protein: Double = 0.0,
    val omega3: Double = 0.0,
    val calories: Double = 0.0,
    val fat: Double = 0.0,
    val carbohydrates: Double = 0.0,
    val fiber: Double = 0.0,
    val vitaminD: Double = 0.0,
    val vitaminB12: Double = 0.0,
    val iron: Double = 0.0,
    val price: Double = 0.0,
    val stockQuantity: Int = 0,
    val unit: String = "kg",
    val imageUrl: String = "",
    val isAvailable: Boolean = true
)

